import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, BarChart3, Calendar, ChevronDown, Loader2,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, User
} from 'lucide-react';

const COLORS = {
  primary: '#1A237E',
  primaryLight: '#3949AB',
  success: '#4CAF50',
  danger: '#F44336',
  warning: '#F9A825',
  white: '#FFFFFF',
  background: '#F0F2F5',
  text: '#212121',
  textLight: '#757575',
  gray: '#9E9E9E',
  grayLight: '#E0E0E0',
};

const AdherenceReportsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [reportType, setReportType] = useState('weekly');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [showPatientPicker, setShowPatientPicker] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/auth'); return; }
    const u = JSON.parse(stored);
    setUser(u);
    loadPatients(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPatients = async (u) => {
    setLoading(true);
    try {
      const url = u.role === 'admin'
        ? `${API}/api/users/admin/${u.id}/elders`
        : `${API}/api/users/caregiver/${u.id}/elders`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setPatients(data);
        if (data.length > 0) {
          const first = data[0];
          const pid = first.user?.id || first.id;
          setSelectedPatient({ id: pid, name: first.user?.name || first.name || 'Patient' });
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (selectedPatient) loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient, reportType]);

  const loadReport = async () => {
    if (!selectedPatient) return;
    setReportLoading(true);
    try {
      const res = await fetch(`${API}/api/adherence/${selectedPatient.id}/${reportType}`);
      if (res.ok) {
        const data = await res.json();
        setReport(data);
      } else {
        setReport(null);
      }
    } catch (err) { console.error(err); setReport(null); }
    finally { setReportLoading(false); }
  };

  const getAdherenceColor = (rate) => {
    if (rate >= 80) return COLORS.success;
    if (rate >= 50) return COLORS.warning;
    return COLORS.danger;
  };

  const AdherenceBar = ({ rate, label }) => (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium" style={{ color: COLORS.text }}>{label}</span>
        <span className="text-sm font-bold" style={{ color: getAdherenceColor(rate) }}>{rate}%</span>
      </div>
      <div className="w-full h-3 rounded-full" style={{ backgroundColor: COLORS.grayLight }}>
        <div className="h-3 rounded-full transition-all duration-500" style={{ width: `${Math.min(rate, 100)}%`, backgroundColor: getAdherenceColor(rate) }} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.background }}>
        <Loader2 className="animate-spin" style={{ color: COLORS.primary }} size={48} />
      </div>
    );
  }

  const summary = report?.summary || {};
  const highlights = report?.highlights || {};
  const trend = report?.trend || {};
  const risk = report?.risk_assessment || {};
  const medicines = report?.medicine_breakdown || [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.background }}>
      {/* Header */}
      <header className="sticky top-0 z-50 shadow-md" style={{ backgroundColor: COLORS.primary }}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-white/10" data-testid="back-btn">
            <ArrowLeft size={22} className="text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg" data-testid="reports-title">Adherence Reports</h1>
            <p className="text-white/70 text-xs">Medicine compliance tracking</p>
          </div>
          <BarChart3 size={24} className="text-white/50" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Patient Selector */}
        <div className="bg-white rounded-xl shadow-sm p-4" data-testid="patient-selector">
          <label className="text-sm font-medium block mb-2" style={{ color: COLORS.textLight }}>Patient</label>
          <div className="relative">
            <button onClick={() => setShowPatientPicker(!showPatientPicker)}
              className="w-full flex items-center justify-between p-3 rounded-lg"
              style={{ backgroundColor: COLORS.background, border: `1px solid ${COLORS.grayLight}` }}
              data-testid="patient-dropdown-btn">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${COLORS.primary}15` }}>
                  <User size={16} style={{ color: COLORS.primary }} />
                </div>
                <span className="font-medium" style={{ color: COLORS.text }}>{selectedPatient?.name || 'Select patient'}</span>
              </div>
              <ChevronDown size={20} style={{ color: COLORS.gray }} />
            </button>
            {showPatientPicker && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto" style={{ border: `1px solid ${COLORS.grayLight}` }}>
                {patients.map((p, i) => {
                  const pu = p.user || p;
                  return (
                    <button key={pu.id || i} onClick={() => { setSelectedPatient({ id: pu.id, name: pu.name }); setShowPatientPicker(false); }}
                      className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left"
                      data-testid={`patient-option-${i}`}>
                      <User size={16} style={{ color: COLORS.primary }} />
                      <span style={{ color: COLORS.text }}>{pu.name || 'Patient'}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Report Type Tabs */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="report-type-tabs">
          <div className="flex">
            {['daily', 'weekly', 'monthly'].map(type => (
              <button key={type} onClick={() => setReportType(type)}
                className="flex-1 py-3 text-sm font-semibold capitalize transition-all"
                data-testid={`tab-${type}`}
                style={{
                  color: reportType === type ? COLORS.primary : COLORS.gray,
                  borderBottom: reportType === type ? `3px solid ${COLORS.primary}` : `1px solid ${COLORS.grayLight}`,
                  backgroundColor: reportType === type ? `${COLORS.primary}05` : 'transparent'
                }}>
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Report Content */}
        {reportLoading ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <Loader2 className="animate-spin mx-auto mb-3" style={{ color: COLORS.primary }} size={32} />
            <p style={{ color: COLORS.textLight }}>Loading report...</p>
          </div>
        ) : report ? (
          <>
            {/* Overall Adherence */}
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="adherence-summary">
              <h3 className="font-bold mb-4" style={{ color: COLORS.text }}>Overall Adherence</h3>
              <div className="flex items-center gap-6 mb-4">
                <div className="w-24 h-24 rounded-full flex items-center justify-center" 
                  style={{ border: `4px solid ${getAdherenceColor(summary.overall_adherence_rate || 0)}`, backgroundColor: `${getAdherenceColor(summary.overall_adherence_rate || 0)}08` }}>
                  <span className="text-2xl font-bold" style={{ color: getAdherenceColor(summary.overall_adherence_rate || 0) }}>
                    {summary.overall_adherence_rate || 0}%
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-lg" style={{ color: COLORS.text }}>{summary.adherence_rating || 'N/A'}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span style={{ color: COLORS.success }}>Taken: {summary.total_taken_doses || 0}</span>
                    <span style={{ color: COLORS.danger }}>Missed: {summary.total_missed_doses || 0}</span>
                    <span style={{ color: COLORS.warning }}>Pending: {summary.total_pending_doses || 0}</span>
                  </div>
                </div>
              </div>

              {/* Trend (weekly/monthly) */}
              {trend.change !== undefined && (
                <div className="flex items-center gap-2 p-3 rounded-lg" 
                  style={{ backgroundColor: trend.trend_direction === 'up' ? `${COLORS.success}10` : `${COLORS.danger}10` }}
                  data-testid="adherence-trend">
                  {trend.trend_direction === 'up' ? <TrendingUp size={18} style={{ color: COLORS.success }} /> : <TrendingDown size={18} style={{ color: COLORS.danger }} />}
                  <span className="text-sm font-medium" style={{ color: trend.trend_direction === 'up' ? COLORS.success : COLORS.danger }}>
                    {trend.change > 0 ? '+' : ''}{trend.change}% from previous period ({trend.previous_adherence}%)
                  </span>
                </div>
              )}
            </div>

            {/* Risk Assessment (if available) */}
            {risk.risk_level && (
              <div className="bg-white rounded-xl shadow-sm p-4" data-testid="risk-assessment">
                <div className="flex items-center gap-3">
                  <AlertTriangle size={20} style={{ color: risk.risk_level === 'high' ? COLORS.danger : risk.risk_level === 'medium' ? COLORS.warning : COLORS.success }} />
                  <div>
                    <p className="font-semibold capitalize" style={{ color: COLORS.text }}>Risk Level: {risk.risk_level}</p>
                    <p className="text-sm" style={{ color: COLORS.textLight }}>Score: {risk.risk_score}/100</p>
                  </div>
                </div>
                {risk.risk_factors?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {risk.risk_factors.map((f, i) => (
                      <li key={f} className="text-sm flex items-start gap-2" style={{ color: COLORS.textLight }}>
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS.warning }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Medicine Breakdown */}
            {medicines.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6" data-testid="medicine-breakdown">
                <h3 className="font-bold mb-4" style={{ color: COLORS.text }}>Medicine Breakdown</h3>
                {medicines.map((med, i) => (
                  <AdherenceBar key={med.medicine_name || i} rate={med.adherence_rate || 0} label={med.medicine_name || `Medicine ${i + 1}`} />
                ))}
              </div>
            )}

            {/* Highlights */}
            {(highlights.best_adherence_day || highlights.worst_adherence_day) && (
              <div className="bg-white rounded-xl shadow-sm p-6" data-testid="report-highlights">
                <h3 className="font-bold mb-4" style={{ color: COLORS.text }}>Highlights</h3>
                <div className="grid grid-cols-2 gap-4">
                  {highlights.best_adherence_day && (
                    <div className="p-3 rounded-lg" style={{ backgroundColor: `${COLORS.success}08` }}>
                      <CheckCircle size={18} style={{ color: COLORS.success }} className="mb-1" />
                      <p className="text-xs" style={{ color: COLORS.textLight }}>Best Day</p>
                      <p className="font-semibold text-sm" style={{ color: COLORS.text }}>{highlights.best_adherence_day}</p>
                    </div>
                  )}
                  {highlights.worst_adherence_day && (
                    <div className="p-3 rounded-lg" style={{ backgroundColor: `${COLORS.danger}08` }}>
                      <AlertTriangle size={18} style={{ color: COLORS.danger }} className="mb-1" />
                      <p className="text-xs" style={{ color: COLORS.textLight }}>Worst Day</p>
                      <p className="font-semibold text-sm" style={{ color: COLORS.text }}>{highlights.worst_adherence_day}</p>
                    </div>
                  )}
                </div>
                {highlights.most_missed_medicine && (
                  <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: `${COLORS.warning}08` }}>
                    <p className="text-xs" style={{ color: COLORS.textLight }}>Most Missed Medicine</p>
                    <p className="font-semibold text-sm" style={{ color: COLORS.text }}>{highlights.most_missed_medicine}</p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center" data-testid="no-report">
            <BarChart3 className="mx-auto mb-3" style={{ color: COLORS.grayLight }} size={48} />
            <p style={{ color: COLORS.textLight }}>No report data available.</p>
            <p className="text-sm mt-1" style={{ color: COLORS.gray }}>Reports will appear once medicine schedules and intake logs are recorded.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdherenceReportsPage;
